// src/features/developers/keys.ts
//
// The logic of the staff key screen, apart from its components: validation of the form, the
// request it sends, the text of each failure and the small formatting the table needs.
import type { AccessKey, CreateKeyRequest } from './types';

export const NAME_MAX = 120;
export const OWNER_MAX = 200;

export interface KeyForm {
  name: string;
  ownerName: string;
  propertyIds: string[];
}

export type KeyFormField = 'name' | 'owner' | 'properties';

/** The text key (under `developers.keys.form.invalid`) of each field that is not valid. */
export const validateKeyForm = (form: KeyForm): Partial<Record<KeyFormField, string>> => {
  const errors: Partial<Record<KeyFormField, string>> = {};
  const name = form.name.trim();
  const owner = form.ownerName.trim();
  if (!name) errors.name = 'name';
  else if (name.length > NAME_MAX) errors.name = 'nameTooLong';
  if (!owner) errors.owner = 'owner';
  else if (owner.length > OWNER_MAX) errors.owner = 'ownerTooLong';
  if (form.propertyIds.length === 0) errors.properties = 'properties';
  return errors;
};

export const toCreateRequest = (form: KeyForm): CreateKeyRequest => ({
  name: form.name.trim(),
  ownerName: form.ownerName.trim(),
  ownerOrganizationId: null,
  propertyIds: Array.from(new Set(form.propertyIds)),
});

const KNOWN_FAILURES = ['INVALID_NAME', 'INVALID_OWNER', 'INVALID_PROPERTIES', 'UNKNOWN_PROPERTIES', 'UNKNOWN_ORGANIZATION'];

/** The text key (under `developers.keys.failures`) for an API failure code. */
export const failureTextKey = (code: string | undefined): string => (code && KNOWN_FAILURES.includes(code) ? code : 'generic');

/** A key in the list after the API revoked it; the rest of the list is untouched. */
export const replaceKey = (keys: AccessKey[], updated: AccessKey): AccessKey[] =>
  keys.map((key) => (key.id === updated.id ? updated : key));

/** What the list holds after a creation: the new key on top, without its secret. */
export const prependKey = (keys: AccessKey[], created: AccessKey): AccessKey[] => {
  const { apiKey: _secret, ...listed } = created as AccessKey & { apiKey?: string };
  return [listed as AccessKey, ...keys.filter((key) => key.id !== created.id)];
};

/** Names of a key's properties for the table; an unknown name falls back to nothing, never to the id. */
export const propertyNames = (key: AccessKey): string[] => key.properties.map((p) => p.name).filter((n): n is string => Boolean(n));
